// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import BloqueFicha from "../components/funcionales/BloqueFicha";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import { traerDiferencias } from "../services/rrhh";
import { etiquetaCorta, claseDeEmpresa } from "../shared/empresa";
import { rutaDePersona } from "../shared/personas";

// ----------------------------------------------------------------------- estilos
import "./css/fichas-pantalla.css";

/* Dónde la nómina y el padrón no dicen lo mismo. Son 92.
 *
 * ── POR QUÉ EXISTE ESTA PANTALLA ────────────────────────────────────────────
 *
 * Hay tres respuestas a «quién trabaja acá» y ninguna coincide:
 *
 *     personas.estado = 'true' ............ 356
 *     con período abierto ................. 256
 *     la nómina de sueldos ................ 292
 *
 * Manda la nómina —es contra lo que se paga— pero las otras dos no se pisan.
 * Un `update` que alineara el padrón sería 92 decisiones tomadas de una y sin
 * que nadie las viera; y el padrón no siempre está mal: el que cobra y figura
 * de baja puede ser un reingreso que nadie cargó, y ahí lo que hay que
 * corregir es el padrón, no la nómina.
 *
 * Así que la pantalla las muestra y no las resuelve. Cada fila es un trabajo
 * de RRHH.
 *
 * ── LOS TRES CASOS ──────────────────────────────────────────────────────────
 *
 *   «cobra y no está en el padrón» — entró y nadie lo dio de alta acá.
 *   «cobra y el padrón lo da de baja» — reingreso sin registrar, o una baja
 *     cargada de más. Nueve de las diez son de Transcan.
 *   «el padrón lo da activo y no cobra» — son 73, y es el montón: bajas que
 *     nadie registró.
 *
 * Los proveedores quedan afuera del tercer caso a propósito —LA TORRE
 * NEUMÁTICOS y SANCHEZ (CARTRUCK) no van a estar nunca en una nómina de
 * sueldos— y por eso la cuenta cruda de 102 baja a 92. Listar diez filas que
 * no se van a poder resolver nunca es ruido permanente, y del ruido nadie mira
 * nada.
 *
 * ── SE AGRUPA POR CASO Y NO SE ORDENA POR NOMBRE ────────────────────────────
 *
 * Porque cada caso se resuelve distinto y de a tandas: los 73 del tercero se
 * bajan mirando la misma pregunta 73 veces. Una lista alfabética mezclada
 * obligaría a cambiar de pregunta en cada fila.
 */

const texto = (v) => String(v ?? "").trim();

/* El puesto de convenio, que son DOS columnas del export de sueldos y se leen
 * juntas: `calificacion` («Empleado») y `categoria` («Administrativo
 * Calificado»). Separadas no dicen nada —«Empleado» solo es casi todo el
 * mundo—, así que se muestran pegadas y en ese orden, que es como está escrito
 * en el recibo.
 *
 * EL TERCER CASO NO TIENE NINGUNA DE LAS DOS, Y NO ES UN HUECO PARA LLENAR:
 * esas 73 filas salen del padrón justamente porque NO están en la nómina. Se
 * dice con todas las letras en vez de con una raya, que se leería como un dato
 * que alguien se olvidó de cargar. */
function puesto(f) {
  const junto = [texto(f.calificacion), texto(f.categoria)]
    .filter(Boolean)
    .join(" ");
  if (junto) return junto;
  return <span className="tenue">no está en la nómina</span>;
}

/* Qué hacer con cada caso, escrito. Sin esto la pantalla es una lista de
 * nombres con una etiqueta que hay que interpretar, y el que la abre por
 * primera vez no sabe qué se espera de él. */
const CASOS = {
  "cobra y no está en el padrón": {
    corto: "Falta en el padrón",
    que: "Cobra sueldo y no tiene ficha. Hay que darlo de alta en el padrón para que se lo pueda asignar a un viaje y cargarle la documentación.",
  },
  "cobra y el padrón lo da de baja": {
    corto: "El padrón lo da de baja",
    que: "Cobra sueldo y la ficha está cerrada. Casi siempre es un reingreso que nadie cargó: hay que reabrir el período.",
  },
  "el padrón lo da activo y no cobra": {
    corto: "Activo y sin sueldo",
    que: "La ficha está abierta y no aparece en ninguna de las cuatro nóminas. Casi siempre es una baja que nadie registró: hay que cerrarle el período con la fecha y el motivo.",
  },
};

const Diferencias = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);

  const [filas, setFilas] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [caso, setCaso] = useState("todos");

  /* Igual que en Nómina: se pide cuando ya se sabe que se puede. */
  const permitido = permisos !== null && permisos.includes("ver.rrhh");

  useEffect(() => {
    if (!permitido) return undefined;
    let vigente = true;
    traerDiferencias()
      .then((d) => vigente && setFilas(d))
      .catch(() => vigente && setError(true));
    return () => {
      vigente = false;
    };
  }, [permitido]);

  const porCaso = useMemo(() => {
    const m = new Map();
    for (const f of filas || []) {
      const k = texto(f.caso);
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [filas]);

  const lista = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return (filas || [])
      .filter((f) => caso === "todos" || texto(f.caso) === caso)
      .filter((f) =>
        !q
          ? true
          : [f.quien, f.dni, f.legajo, f.estado_padron, f.categoria, f.calificacion]
              .map((v) => texto(v).toLowerCase())
              .some((v) => v.includes(q)),
      );
  }, [filas, buscar, caso]);

  /* Agrupadas para dibujar: el encabezado de cada caso explica qué hacer, y
   * eso no se puede repetir fila por fila. */
  const grupos = useMemo(() => {
    const m = new Map();
    for (const f of lista) {
      const k = texto(f.caso);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    }
    return [...m.entries()];
  }, [lista]);

  if (!puede("ver.rrhh")) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      <div className="tk-filtros">
        <input
          type="search"
          placeholder="Buscar por nombre, DNI o legajo"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          aria-label="Buscar en las diferencias"
        />
        <button
          type="button"
          className={"tk-chip" + (caso === "todos" ? " activo" : "")}
          onClick={() => setCaso("todos")}
        >
          Todas{filas !== undefined && ` ${filas.length}`}
        </button>
        {[...porCaso.entries()].map(([k, n]) => (
          <button
            key={k}
            type="button"
            className={"tk-chip" + (caso === k ? " activo" : "")}
            onClick={() => setCaso(k)}
            title={CASOS[k]?.que || k}
          >
            {CASOS[k]?.corto || k} {n}
          </button>
        ))}
      </div>

      {/* CERO DIFERENCIAS ES UNA BUENA NOTICIA Y NO UNA PANTALLA ROTA. Pero
          también es lo que se ve si la nómina nunca se importó, así que los
          dos casos se dicen por separado: mandar a festejar cuando en realidad
          falta cargar el archivo es el peor error posible acá. */}
      {filas !== undefined && filas.length === 0 && (
        <div className="panel-aviso suave">
          <h3>No hay diferencias</h3>
        </div>
      )}

      {filas !== undefined && filas.length > 0 && lista.length === 0 && (
        <div className="panel-aviso suave">
          <h3>Nada con ese filtro</h3>
          <p className="panel-aviso__chico">
            Probá con otro texto o tocá «Todas».
          </p>
        </div>
      )}

      {/* El marco es `BloqueFicha`, el mismo de las fichas, y no un `section`
          nuevo: es exactamente lo que hace —un título con contenido debajo— y
          una segunda forma de dibujar lo mismo son dos definiciones de cómo se
          ve un bloque en este hub. */}
      {grupos.map(([k, filasDelCaso]) => (
        <BloqueFicha
          key={k}
          titulo={`${CASOS[k]?.corto || k} · ${filasDelCaso.length}`}
          estado="listo"
        >
          {/* Qué hacer, arriba de la lista. Una etiqueta sola obliga a que
              alguien le explique la pantalla al que entra. */}
          <p className="tenue">{CASOS[k]?.que || k}</p>
          <div className="tabla-wrap">
            <table className="tabla fija">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th>DNI</th>
                  <th>Puesto</th>
                  <th>Legajo</th>
                  <th>Empresa</th>
                  <th>Estado en el padrón</th>
                </tr>
              </thead>
              <tbody>
                {filasDelCaso.map((f) => (
                  <tr key={`${k}-${f.dni}`}>
                    <td>
                      {/* Al nombre se llega a la ficha, que es donde se ve el
                          historial y se puede entender qué pasó. Sin el
                          enlace, resolver una diferencia obligaría a copiar el
                          DNI y buscarlo en otra pantalla — 92 veces. */}
                      {rutaDePersona(f.dni) ? (
                        <Link className="rto-link" to={rutaDePersona(f.dni)}>
                          {texto(f.quien) || "(sin nombre)"}
                        </Link>
                      ) : (
                        texto(f.quien) || "(sin nombre)"
                      )}
                    </td>
                    <td className="dato">{texto(f.dni) || "—"}</td>
                    {/* EL PUESTO VA AL LADO DEL DNI Y NO AL FINAL. Decide si la
                        fila es tuya: «hay que darlo de alta para asignarlo a un
                        viaje y cargarle la documentación» es la instrucción
                        correcta para un chofer y la equivocada para un lavador,
                        y seis de los nueve del primer caso no manejan. Leído
                        recién en la última columna, el trabajo ya se empezó. */}
                    <td className="dif-puesto">{puesto(f)}</td>
                    <td className="dato">{texto(f.legajo) || "—"}</td>
                    <td>
                      <span className={"pill " + claseDeEmpresa(f.empresa)}>
                        {etiquetaCorta(f.empresa)}
                      </span>
                    </td>
                    <td className="sub">{texto(f.estado_padron) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </BloqueFicha>
      ))}
    </>
  );
};

export default Diferencias;
